/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./VIPRDecoratable',
	'./VIPRValue'
], function(_, VIPRDecoratable, VIPRValue) {

	// implements IDataPoint
	class VIPRDataPoint extends VIPRDecoratable {
		/**
		 * @param {Number} index - the index of this datapoint
		 * @param {Object} dataset - the ViprDataSet object
		 * @param {Object} dataIteminfo - an object that describes whether dataItems are latlong or have format callbacks.
		 * @param {Boolean[]} dataItemInfo.isLatLong - true if this item denotes a lattitude or longitude value
		 * @param {Function[]} dataItemInfo.formatter - set if the VIPRDataItem has a formatter													
		 * @param {Object} context - the context.
		 */
		constructor(index, dataset, dataItemInfo, context) {
			super({ getDecorationsCallback: dataset.queryResult.getPointDecorations.bind(dataset.queryResult, index) }, context);

			this.index = index;
			this.dataset = dataset;
			this.dataItemInfo = dataItemInfo;
			this.context = context;

			this.queryResult = dataset.queryResult;

			this.viprValues = {};	//Keep any viprValue (IValue) objects.
		}

		destroy() {
			for(const key in this.viprValues) {
				this.viprValues[key].destroy();
				delete this.viprValues[key];
			}
			this.dataset = null;
			this.viprValues = null;
			this.context = null;
		}

		/**
		 * @param {Number} resultItemIndex - the index of this categorical resultItem in the QueryResult AND the VIPRDataSet
		 * @returns {Number} the index of the tuple for this item that this datapoint represents.
		 */
		getTupleIndex(resultItemIndex) {
			let tupleIndex = this.queryResult.getValueIndex(this.index, resultItemIndex);
			// Keep a reverse-reference from tuple to the datapoint
			const viprDataItem = this.dataset.getDataItem && this.dataset.getDataItem(resultItemIndex);
			viprDataItem && viprDataItem.getTuple(tupleIndex).addDataPoint(this);
			return tupleIndex;
		}

		/**
		 * @param {Number} resultItemIndex - the index of this continuous resultItem in the QueryResult AND the VIPRDataSet
		 * @returns {Object} A VIPRValue API which includes a getValue() getCaption() to get the actual values.
		 */
		getValue(resultItemIndex) {
			if (this.viprValues[resultItemIndex]===undefined) {
				let valueObj = this.queryResult.getValue(this.index, resultItemIndex);
				if (this.dataItemInfo.isLatLong[resultItemIndex]) {
					// For values which are lat/long, the datapoint contains the index in the resultItem values
					// where the lat/long is stored.  
					// (using getValue hides this detail except that the value is returned as a tuple array of length 1)
					// Lat/long value as a tuple can be either an number or string
					valueObj = typeof valueObj[0].value === 'number' ? valueObj[0].value : parseFloat(valueObj[0].label);
				}
				this.viprValues[resultItemIndex]=new VIPRValue(valueObj, this.dataItemInfo.formatter[resultItemIndex], resultItemIndex);
			}
			return this.viprValues[resultItemIndex];
		}

		/**
		 * @description - Decorate this datapoint with the named decoration
		 * @param {string} name - The name of the decoration for this datapoint (eg. selected, highlighted)
		 * @returns {Boolean} value - the value of the decoration (usu true, false or undefined)
		 */
		decorate(name, value) {
			value = typeof value === 'undefined' ? true : value;
			super.decorate(name, value);
		}

		/**
		 * @returns The full, evaluated tuple for the categorical items in this datapoint. 
		 */
		getTupleItems() {
			const items = [];
			this.dataset.dataItems.forEach((dataItem, i) => {
				if (dataItem.getType()==='cat' || this.dataItemInfo.isLatLong[i]) {
					const value = this.queryResult.getValue(this.index, i);
					if (Array.isArray(value)) {
						items.push(...value);
					}
				}
			});
			return items;
		}

		/**
		 * @description This additional api returns a key for the datapoint based on the valueIndexes of each categorical tuple
		 * @example used to match query result tuples against points in smartAnnotation responses for decorating - see VIPRView)
		 * @returns {String} - a "key" of form 'n,m,...' where n and m are the tupleIndexes of each categorical item.
		 */
		getDataPointKey() {
			const tupleIndexes = [];
			this.dataset.dataItems.forEach((dataItem, i) => {
				if (dataItem.getType()==='cat' || this.dataItemInfo.isLatLong[i]) {
					tupleIndexes.push(this.queryResult.getValueIndex(this.index, i));
				}
			});
			return tupleIndexes.toString();
		}
	}

	return VIPRDataPoint;
});
